import {
  App,
  Button,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import {
  DollarOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  RollbackOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { inventoryApi, type Vendor, type VendorInput } from '@/api/inventory';
import { financeApi } from '@/api/finance';
import { setupApi } from '@/api/setup';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';
import { useReasonPrompt } from '@/shared/hooks/use-reason-prompt';

const PKR = (n: number | string) =>
  Number(n).toLocaleString('en-PK', { maximumFractionDigits: 2 });

interface FormShape {
  name: string;
  vendorTypeId: number;
  contactPerson?: string;
  phone?: string;
  address?: string;
  cnicOrNtn?: string;
  bankName?: string;
  bankAccountNumber?: string;
  openingBalance?: number;
  openingBalanceDate?: Dayjs;
}

export function VendorsPage() {
  const { message, modal } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('INVENTORY:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('INVENTORY:EDIT'));
  const canDelete = useAuthStore((s) => s.hasAuthority('INVENTORY:DELETE'));

  const [editing, setEditing] = useState<Vendor | null>(null);
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState<Vendor | null>(null);
  const [form] = Form.useForm<FormShape>();

  const { data, isLoading } = useQuery({
    queryKey: ['inventory', 'vendors'],
    queryFn: () => inventoryApi.listVendors(),
  });

  const { data: types } = useQuery({
    queryKey: ['setup', 'lookups', 'vendor-types', 'active'],
    queryFn: () => setupApi.listLookup('vendor-types', true),
    staleTime: 5 * 60_000,
  });

  const upsert = useMutation({
    mutationFn: (values: FormShape) => {
      const body: VendorInput = {
        name: values.name.trim(),
        vendorTypeId: values.vendorTypeId,
        contactPerson: values.contactPerson?.trim() || undefined,
        phone: values.phone?.trim() || undefined,
        address: values.address?.trim() || undefined,
        cnicOrNtn: values.cnicOrNtn?.trim() || undefined,
        bankName: values.bankName?.trim() || undefined,
        bankAccountNumber: values.bankAccountNumber?.trim() || undefined,
        openingBalance: values.openingBalance != null ? Number(values.openingBalance).toFixed(2) : '0.00',
        openingBalanceDate: values.openingBalanceDate ? values.openingBalanceDate.format('YYYY-MM-DD') : null,
      };
      return editing ? inventoryApi.updateVendor(editing.id, body) : inventoryApi.createVendor(body);
    },
    onSuccess: () => {
      message.success(editing ? 'Vendor updated' : 'Vendor created');
      qc.invalidateQueries({ queryKey: ['inventory', 'vendors'] });
      setEditing(null);
      setCreating(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const remove = useMutation({
    mutationFn: (id: number) => inventoryApi.deleteVendor(id),
    onSuccess: () => {
      message.success('Vendor removed');
      qc.invalidateQueries({ queryKey: ['inventory', 'vendors'] });
    },
    onError: (e) => message.error(extractError(e)),
  });

  const openCreate = () => {
    setEditing(null);
    setCreating(true);
    form.resetFields();
  };
  const openEdit = (row: Vendor) => {
    setCreating(false);
    setEditing(row);
    form.setFieldsValue({
      name: row.name,
      vendorTypeId: row.vendorTypeId,
      contactPerson: row.contactPerson ?? undefined,
      phone: row.phone ?? undefined,
      address: row.address ?? undefined,
      cnicOrNtn: row.cnicOrNtn ?? undefined,
      bankName: row.bankName ?? undefined,
      bankAccountNumber: row.bankAccountNumber ?? undefined,
      openingBalance: Number(row.openingBalance),
      openingBalanceDate: row.openingBalanceDate ? dayjs(row.openingBalanceDate) : undefined,
    });
  };
  const open = creating || editing != null;

  const columns: TableProps<Vendor>['columns'] = [
    {
      title: 'Vendor',
      render: (_, row) => (
        <Space orientation="vertical" size={0}>
          <Typography.Text strong>{row.name}</Typography.Text>
          <Space size={6}>
            {row.vendorTypeName && <Tag>{row.vendorTypeName}</Tag>}
            {row.contactPerson && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {row.contactPerson}
              </Typography.Text>
            )}
          </Space>
        </Space>
      ),
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      width: 150,
      render: (p: string | null) => p ?? <span style={{ opacity: 0.4 }}>—</span>,
    },
    {
      title: 'Opening',
      dataIndex: 'openingBalance',
      width: 130,
      align: 'right',
      render: (v: string) => `PKR ${PKR(v)}`,
    },
    {
      title: 'Credit pur.',
      dataIndex: 'creditPurchases',
      width: 130,
      align: 'right',
      render: (v: string) => `PKR ${PKR(v)}`,
    },
    {
      title: 'Paid',
      dataIndex: 'totalPaid',
      width: 130,
      align: 'right',
      render: (v: string) => `PKR ${PKR(v)}`,
    },
    {
      title: 'Payable',
      dataIndex: 'payable',
      width: 150,
      align: 'right',
      render: (v: string) => {
        const n = Number(v);
        return (
          <Tag color={n > 0 ? 'red' : n < 0 ? 'success' : 'default'} style={{ fontWeight: 600 }}>
            PKR {PKR(v)}
          </Tag>
        );
      },
    },
    {
      title: 'Actions',
      width: 200,
      align: 'right',
      render: (_, row) => (
        <Space size={4}>
          <Button size="small" type="text" icon={<EyeOutlined />} onClick={() => setViewing(row)}>
            Open
          </Button>
          {canEdit && (
            <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          )}
          {canDelete && (
            <Button
              size="small"
              type="text"
              danger
              onClick={() =>
                modal.confirm({
                  title: `Remove ${row.name}?`,
                  content: 'Soft delete. Only allowed if no outstanding credit purchases.',
                  okType: 'danger',
                  onOk: () => remove.mutate(row.id),
                })
              }
            >
              Delete
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Inventory"
        title="Vendors"
        description="Suppliers and their outstanding payable balance."
        count={{ value: data?.length ?? 0, label: 'vendors' }}
        actions={
          canCreate ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New vendor
            </Button>
          ) : null
        }
      />

      <Table
        rowKey="id"
        size="middle"
        loading={isLoading}
        columns={columns}
        dataSource={data ?? []}
        pagination={{ pageSize: 25 }}
      />

      <Drawer
        title={editing ? `Edit · ${editing.name}` : 'New vendor'}
        open={open}
        onClose={() => {
          setEditing(null);
          setCreating(false);
        }}
        size={520}
        destroyOnHidden
        extra={
          <Space>
            <Button
              onClick={() => {
                setEditing(null);
                setCreating(false);
              }}
            >
              Cancel
            </Button>
            <Button type="primary" loading={upsert.isPending} onClick={() => form.submit()}>
              {editing ? 'Save' : 'Create'}
            </Button>
          </Space>
        }
      >
        {open && (
          <Form form={form} layout="vertical" onFinish={(v) => upsert.mutate(v)}>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item label="Name" name="name" rules={[{ required: true, max: 200 }]} style={{ flex: 1 }}>
                <Input autoFocus placeholder="Vendor name" />
              </Form.Item>
              <Form.Item label="Type" name="vendorTypeId" rules={[{ required: true }]} style={{ width: 200 }}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={(types ?? []).map((t) => ({ value: t.id, label: t.name }))}
                />
              </Form.Item>
            </Space.Compact>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item label="Contact person" name="contactPerson" rules={[{ max: 200 }]} style={{ flex: 1 }}>
                <Input />
              </Form.Item>
              <Form.Item label="Phone" name="phone" rules={[{ max: 20 }]} style={{ width: 200 }}>
                <Input />
              </Form.Item>
            </Space.Compact>
            <Form.Item label="CNIC / NTN" name="cnicOrNtn" rules={[{ max: 20 }]}>
              <Input />
            </Form.Item>
            <Form.Item label="Address" name="address" rules={[{ max: 2000 }]}>
              <Input.TextArea autoSize={{ minRows: 1, maxRows: 3 }} />
            </Form.Item>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item label="Bank" name="bankName" rules={[{ max: 100 }]} style={{ flex: 1 }}>
                <Input />
              </Form.Item>
              <Form.Item label="Account #" name="bankAccountNumber" rules={[{ max: 30 }]} style={{ width: 200 }}>
                <Input />
              </Form.Item>
            </Space.Compact>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item
                label="Opening balance (PKR)"
                name="openingBalance"
                tooltip="Outstanding payable to this vendor at go-live"
                style={{ flex: 1 }}
              >
                <InputNumber style={{ width: '100%' }} step={1000} min={0} />
              </Form.Item>
              <Form.Item label="As of" name="openingBalanceDate" style={{ width: 200 }}>
                <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
              </Form.Item>
            </Space.Compact>
          </Form>
        )}
      </Drawer>

      {viewing && <VendorStatementDrawer vendor={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

function VendorStatementDrawer({ vendor, onClose }: { vendor: Vendor; onClose: () => void }) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const askReason = useReasonPrompt();
  const [paying, setPaying] = useState(false);
  const [payForm] = Form.useForm<{
    paymentDate: Dayjs;
    amount: number;
    bankAccountId: number;
    reference?: string;
    remarks?: string;
  }>();

  const { data: payments, isFetching } = useQuery({
    queryKey: ['inventory', 'vendors', vendor.id, 'payments'],
    queryFn: () => inventoryApi.paymentsForVendor(vendor.id),
  });

  const { data: bankAccounts } = useQuery({
    queryKey: ['finance', 'bank-accounts'],
    queryFn: financeApi.listBankAccounts,
    staleTime: 60_000,
  });

  const pay = useMutation({
    mutationFn: (v: {
      paymentDate: Dayjs;
      amount: number;
      bankAccountId: number;
      reference?: string;
      remarks?: string;
    }) =>
      inventoryApi.addVendorPayment({
        vendorId: vendor.id,
        paymentDate: v.paymentDate.format('YYYY-MM-DD'),
        amount: Number(v.amount).toFixed(2),
        bankAccountId: v.bankAccountId,
        reference: v.reference?.trim() || undefined,
        remarks: v.remarks?.trim() || undefined,
      }),
    onSuccess: () => {
      message.success('Payment recorded and posted to ledger');
      qc.invalidateQueries({ queryKey: ['inventory'] });
      setPaying(false);
      payForm.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const reverse = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      inventoryApi.reverseVendorPayment(id, reason),
    onSuccess: () => {
      message.success('Payment reversed');
      qc.invalidateQueries({ queryKey: ['inventory'] });
    },
    onError: (e) => message.error(extractError(e)),
  });

  return (
    <Drawer open onClose={onClose} title={vendor.name} size={620}>
      <Space orientation="vertical" style={{ width: '100%' }} size={14}>
        <Space wrap>
          {vendor.vendorTypeName && <Tag>{vendor.vendorTypeName}</Tag>}
          <Tag color={Number(vendor.payable) > 0 ? 'red' : 'success'} style={{ fontSize: 13 }}>
            Payable · PKR {PKR(vendor.payable)}
          </Tag>
        </Space>
        <Typography.Text type="secondary">
          Opening PKR {PKR(vendor.openingBalance)} + credit purchases PKR {PKR(vendor.creditPurchases)} − paid PKR {PKR(vendor.totalPaid)}
        </Typography.Text>

        <Space>
          <Button type="primary" icon={<DollarOutlined />} onClick={() => {
            payForm.resetFields();
            payForm.setFieldsValue({ paymentDate: dayjs() });
            setPaying(true);
          }}>
            Record payment
          </Button>
        </Space>

        <Typography.Text strong>Payment history</Typography.Text>
        <Table
          rowKey="id"
          size="small"
          loading={isFetching}
          dataSource={payments ?? []}
          pagination={false}
          columns={[
            {
              title: 'Date',
              dataIndex: 'paymentDate',
              width: 110,
              render: (d: string) => dayjs(d).format('D MMM YYYY'),
            },
            {
              title: 'Amount',
              dataIndex: 'amount',
              width: 130,
              align: 'right',
              render: (a: string, row) => (
                <Typography.Text strong delete={row.reversed}>PKR {PKR(a)}</Typography.Text>
              ),
            },
            {
              title: 'Account',
              dataIndex: 'bankAccountName',
              render: (n: string | null) => n ?? '—',
            },
            {
              title: 'Ref',
              dataIndex: 'reference',
              render: (r: string | null) => r ?? '—',
            },
            {
              title: '',
              width: 90,
              align: 'right',
              render: (_, row) =>
                row.reversed ? (
                  <Tag color="default">Reversed</Tag>
                ) : (
                  <Button
                    size="small"
                    type="text"
                    icon={<RollbackOutlined />}
                    onClick={async () => {
                      const reason = await askReason({
                        title: 'Reverse vendor payment?',
                        placeholder: 'Why is this payment being reversed?',
                        okText: 'Reverse payment',
                        danger: true,
                      });
                      if (reason) reverse.mutate({ id: row.id, reason });
                    }}
                  >
                    Reverse
                  </Button>
                ),
            },
          ]}
        />
      </Space>

      <Modal
        title={`Pay ${vendor.name}`}
        open={paying}
        onCancel={() => setPaying(false)}
        onOk={() => payForm.submit()}
        confirmLoading={pay.isPending}
        okText="Record"
        destroyOnHidden
      >
        {paying && (
          <Form form={payForm} layout="vertical" onFinish={(v) => pay.mutate(v)}>
            <Form.Item label="Date" name="paymentDate" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
            </Form.Item>
            <Form.Item label="Amount (PKR)" name="amount" rules={[{ required: true, type: 'number', min: 0.01 }]}>
              <InputNumber style={{ width: '100%' }} step={1000} min={0} />
            </Form.Item>
            <Form.Item label="Pay from" name="bankAccountId" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                options={(bankAccounts ?? []).map((b) => ({
                  value: b.id,
                  label: b.accountName + (b.bankName ? ` · ${b.bankName}` : ''),
                }))}
              />
            </Form.Item>
            <Form.Item label="Reference" name="reference" rules={[{ max: 200 }]}>
              <Input placeholder="Cheque #, online ref, etc." />
            </Form.Item>
            <Form.Item label="Remarks" name="remarks" rules={[{ max: 2000 }]}>
              <Input.TextArea autoSize={{ minRows: 1, maxRows: 3 }} />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </Drawer>
  );
}
